import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from '../../lib/safeToast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getKycBadgeClass = (kyc) => {
  switch (kyc) {
    case 'approved': return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'in_review': return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'rejected': return 'border-red-200 bg-red-50 text-red-700';
    default: return 'border-slate-200 bg-slate-50 text-slate-600';
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'active': return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'suspended': return 'border-amber-200 bg-amber-50 text-amber-700';
    default: return 'border-slate-200 bg-slate-50 text-slate-600';
  }
};

const DEBOUNCE_MS = 350;

const BackOfficeFinanceMerchants = () => {
  const { isLive } = useEnvironment();
  const env = isLive ? 'live' : 'sandbox';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const fetchMerchants = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ per_page: 100 });
        if (search) params.set('search', search);
        const res = await fetch(`${API_URL}/api/backoffice/merchants?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-Environment': env,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json?.data || []);
      } catch (err) {
        toast.error('Failed to load merchant list');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMerchants();
  }, [env, search]);

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
  };

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="spinner w-8 h-8" />
          <p className="text-sm text-slate-500">Loading merchant accounts…</p>
        </div>
      </BackOfficeLayout>
    );
  }

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Users className="w-7 h-7 text-[#0B6445]" />
            Merchant Accounts
          </h1>
          <p className="text-[#0E1B16]/70 mt-2 mb-6">
            Read-only view of all merchant accounts, KYC status, and account status.
          </p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <form onSubmit={(e) => e.preventDefault()} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0E1B16]/50" />
                <Input
                  placeholder="Search by name or email…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                  aria-label="Search merchants"
                />
              </div>
              {searchInput && (
                <Button type="button" variant="outline" onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </form>
          </CardHeader>
          <CardContent>
            {data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No merchants found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Name</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">KYC Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Account Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((m) => (
                      <tr key={m.id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-2 font-medium text-[#073B2E]">{m.name || '—'}</td>
                        <td className="py-3 px-2 text-[#0E1B16]/70">{m.email || '—'}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={getKycBadgeClass(m.kyc_status)}>
                            {(m.kyc_status || 'pending').replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={getStatusBadgeClass(m.account_status)}>
                            {m.account_status === 'suspended' ? 'Inactive' : m.account_status || 'active'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-[#0E1B16]/70">
                          {m.created_at ? new Date(m.created_at).toLocaleDateString('en-AU') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeFinanceMerchants;
